import { createContext, useContext, type FC, type ReactNode } from "react";

/**
 * Carries the id of the nearest enclosing `<Templated>` shell down to its
 * descendant `<Slot>` markers, so each Slot can resolve drop-on-override
 * semantics against the v2 claim map ("is this slot claimed by a template
 * other than mine?").
 *
 * `null` outside any Templated shell — Slots used in that position are
 * suppressed whenever the slot id is claimed anywhere in any embedder
 * template (every claim is "elsewhere" from the perspective of a slot with
 * no enclosing Templated).
 */
export const TemplatedScope = createContext<string | null>(null);

interface TemplatedScopeProviderProps {
  templateId: string;
  children: ReactNode;
}

export const TemplatedScopeProvider: FC<TemplatedScopeProviderProps> = ({
  templateId,
  children,
}) => (
  <TemplatedScope.Provider value={templateId}>{children}</TemplatedScope.Provider>
);

/**
 * Read the nearest enclosing Templated id (or `null` if none).
 */
export const useTemplatedScope = (): string | null =>
  useContext(TemplatedScope);
